import React from 'react';

import { DiffFile } from 'src/components/diff-set/base-diff-file';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import {
  FileContentsUnchangedDiff,
  BinaryDiff,
  ExcessiveDiff,
  EmptyDiff,
  HiddenDiff,
  ExcludedDiff,
  PartialDiff,
} from 'src/components/diff-set/hidden-diffs/index';
import ImageDiff from 'src/components/image-diffs/src/image-diff';
import { useIntl } from 'src/hooks/intl';
import messages from 'src/sections/repository/sections/pull-request/components/diff.i18n';
import { useCommentDetailsForFile } from 'src/sections/repository/sections/pull-request/hooks/comment-details-for-file';
import { useDiffExpansionState } from 'src/sections/repository/sections/pull-request/hooks/diff-expansion-state';
import { Diff } from 'src/types/pull-request';
import {
  isImageDiff,
  isBinaryDiff,
  isFileRenamedDiff,
  isEmptyDiff,
  isPartialDiff,
  isSingleDiffTimeout,
} from 'src/utils/diff-classifications';
import { extractFilepath } from 'src/utils/extract-file-path';
import { getExcessiveDiffSize } from 'src/utils/get-excessive-diff-size';
import { getExcludedPattern } from 'src/utils/get-excluded-pattern';

type Props = {
  file: Diff;
  index: number;
  diffMetadataProps: DiffMetadataProps;
};

export const ClassifiedDiff = ({ file, index, diffMetadataProps }: Props) => {
  const intl = useIntl();
  const filepath = extractFilepath(file);

  const diffExpansionState = useDiffExpansionState({ filepath });

  // This logic only applies to the pull request diff
  // untouchedByUser should always be true for branches and commits
  const wasExpandedByUser = diffExpansionState === true;
  const untouchedByUser = diffExpansionState === undefined;

  const { hasCurrentComments } = useCommentDetailsForFile(file);

  // This logic only applies to the pull request diff,
  // because branches don't have comments and commits can't have outdated comments
  const isOpen = wasExpandedByUser || (untouchedByUser && hasCurrentComments);

  if (isPartialDiff(file)) {
    return <PartialDiff file={file} />;
  }

  if (isSingleDiffTimeout(file)) {
    return (
      <ExcessiveDiff
        file={file}
        isOpen={isOpen}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  if (file.isFileContentsUnchanged) {
    return (
      <FileContentsUnchangedDiff
        file={file}
        isOpen={isOpen}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  const excludedPattern = getExcludedPattern(file);
  if (excludedPattern) {
    return (
      <ExcludedDiff
        file={file}
        pattern={excludedPattern}
        isOpen={isOpen}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  const excessiveDiff = getExcessiveDiffSize(file);
  if (excessiveDiff) {
    return (
      <ExcessiveDiff
        file={file}
        size={excessiveDiff}
        isOpen={isOpen}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  if (isFileRenamedDiff(file)) {
    return (
      <HiddenDiff
        file={file}
        isOpen={isOpen}
        heading={intl.formatMessage(messages.fileRenamed)}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  if (isImageDiff(file)) {
    return (
      <ImageDiff
        file={file}
        filepath={filepath}
        isOpen={isOpen || (untouchedByUser && !file.deleted)}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  if (isBinaryDiff(file)) {
    return (
      <BinaryDiff
        file={file}
        isOpen={isOpen}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  if (isEmptyDiff(file)) {
    return (
      <EmptyDiff
        file={file}
        isOpen={isOpen}
        diffMetadataProps={diffMetadataProps}
      />
    );
  }

  return (
    <DiffFile
      file={file}
      fileIndex={index}
      diffMetadataProps={diffMetadataProps}
      isOpen={isOpen || (untouchedByUser && !file.deleted)}
    />
  );
};
